import { useEffect, useState } from "react";
import { Heart, HouseLine, UsersThree, ShieldCheck, Wallet, ArrowRight } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { DesignatedCaregiverCard } from "../components/DesignatedCaregiverCard";

export default function DesignatedCaregiverPage() {
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const isStaff = user && user.role !== "admin";

  const load = () => {
    if (isStaff) api.get(`/employees/${user.id}/file`).then((r) => setFile(r.data)).catch(() => setFile(false));
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="mx-auto max-w-2xl px-4 py-8" data-testid="dc-explainer-page">
      <p className="text-xs font-semibold uppercase tracking-widest text-[#B7791F]">Silver Home Care</p>
      <h1 className="mt-1 font-heading text-3xl tracking-tight text-[#4A235A] sm:text-4xl" data-testid="dc-title">
        Caring for someone you love? You can be their paid caregiver.
      </h1>
      <p className="mt-3 text-base leading-relaxed text-stone-600">
        It's called a <strong>designated caregiver</strong> arrangement — and through Pennsylvania's
        <strong> Community HealthChoices</strong> program, many people who need in-home care can choose someone they
        already know and trust: a family member, a friend, a neighbor. That person gets hired, trained, and <em>paid</em>
        for the care they're already giving.
      </p>

      <div className="mt-8 space-y-4">
        {[
          { Icon: HouseLine, title: "What Community HealthChoices is",
            body: "Community HealthChoices (CHC) is Pennsylvania's Medicaid managed-care program for seniors and adults with physical disabilities. It covers in-home personal care — help with meals, bathing, dressing, mobility, and daily life — so people can stay in their own homes." },
          { Icon: UsersThree, title: "What a designated caregiver is",
            body: "Instead of a stranger, the client picks a person they already know. That person becomes a real Silver Home Care employee: application, background checks, orientation, training — the whole thing — and then works scheduled, documented shifts caring for their loved one." },
          { Icon: Wallet, title: "Care you're already giving can become a paycheck",
            body: "If you're helping a parent, grandparent, or friend with daily life right now — unpaid — they may qualify for services through CHC, and you may be able to become their paid designated caregiver. Many caregivers don't know this exists. If it sounds like your situation, tell the office." },
          { Icon: ShieldCheck, title: "One important legal rule",
            body: "CHC waiver funds cannot pay a caregiver who is the client's spouse, Power of Attorney, legal guardian, or representative payee — while any of those roles applies, the caregiver isn't eligible to provide paid CHC waiver services to that client. The office will help you sort out whether this applies to you." },
        ].map(({ Icon, title, body }, i) => (
          <div key={i} className="flex gap-4 rounded-xl border border-stone-200 bg-white p-5 shadow-sm" data-testid={`dc-block-${i}`}>
            <Icon size={28} className="mt-0.5 shrink-0 text-[#4A235A]" />
            <div>
              <p className="font-heading text-lg tracking-tight text-stone-900">{title}</p>
              <p className="mt-1 text-sm leading-relaxed text-stone-600">{body}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 rounded-xl border border-[#B7791F]/30 bg-[#B7791F]/5 p-5">
        <p className="flex items-center gap-2 font-heading text-lg tracking-tight text-stone-900">
          <Heart size={20} weight="fill" className="text-[#B7791F]" /> Think this might be you?
        </p>
        <p className="mt-1 text-sm text-stone-600">
          Tell the office — a quick conversation is all it takes to find out whether the person you care for qualifies
          and whether you could be their paid designated caregiver. There's no obligation.
        </p>
      </div>

      {isStaff && file && (
        <div className="mt-8" data-testid="dc-answer-section">
          <p className="font-heading text-lg tracking-tight text-stone-900">Your answer, on the record</p>
          <p className="mt-1 mb-3 text-sm text-stone-500">Every employee answers this once — yes or no. It takes a minute.</p>
          <DesignatedCaregiverCard uid={user.id} emp={file} startOpen onSaved={load} />
        </div>
      )}
      {isStaff && !file && (
        <p className="mt-8 text-sm text-stone-400" data-testid="dc-file-unavailable">Loading your record…</p>
      )}

      <p className="mt-10 flex items-center gap-1 text-xs text-stone-400">
        Questions? The office is happy to explain — no forms needed to just ask. <ArrowRight size={12} />
      </p>
    </div>
  );
}
